import React, { useEffect, useState } from 'react'
import GenericPromises from '../../../../api/GenericPromises';
import { useNotificationsStore } from '../../../../store/Notifications/NotificationsStore';
import { useTranslation } from 'react-i18next';
import { NotificationStatus } from '../../../../interfaces/Commons/Notifications/notificationStatuses';
import { Notification } from '../../../../interfaces/Commons/Notifications/notifications';
import { Autocomplete, Avatar, Box, Button, Checkbox, Grid, IconButton, List, ListItem, ListItemAvatar, ListItemText, Menu, MenuItem, TextField, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import useSnackBar from '../../../Commons/SnackBar/useSnackBar';
import { useNotificationStatusStore } from '../../../../store/Notifications/NotificationStatusStore';
import FilterListIcon from '@mui/icons-material/FilterList';
import ReplayIcon from '@mui/icons-material/Replay';
import { AuthContext } from '../../../../context/AuthContext';
import { useFetchNotifications } from '../../../../hooks/useFetchNotifications';
import { NotificationList } from './components/NotificationList';
import FilterMenu from './components/FilterMenu';
import ActionsMenu from './components/ActionsMenu';



export const Notifications = () => {
    const { GenericPutResource, GenericGetResource } = GenericPromises();
    const notifications = useNotificationsStore(state => state.notifications);
    const markAsRead = useNotificationsStore(state => state.markAsRead);
    const markAsUnRead = useNotificationsStore(state => state.markAsUnRead);
    const deleteNotification = useNotificationsStore(state => state.deleteNotification);
    const filterNotificationsByStatus = useNotificationsStore(state => state.filterNotificationsByStatus);
    const filteredNotifications = useNotificationsStore(state => state.filteredNotifications);

    const notificationStatuses = useNotificationStatusStore(state => state.notificationStatuses);
    const addNotificationStatus = useNotificationStatusStore(state => state.addNotificationStatus);
    const readStatus = useNotificationStatusStore(store => store.readStatus);
    const unreadStatus = useNotificationStatusStore(store => store.unreadStatus);
    const deletedStatus = useNotificationStatusStore(store => store.deleteStatus);
    const { showSnackBar, SnackbarComponent } = useSnackBar();
    const [t] = useTranslation("global");
    const [loadingPost, setLoadingPost] = useState(false);
    const allStatus: NotificationStatus = { notification_status_name: "Todos", notification_status_id: 0 };
    const [selectedStatus, setSelectedStatus] = useState<NotificationStatus | null>(allStatus);
    const [selectedNotifications, setSelectedNotifications] = useState<Notification[]>([]);
    const [anchorElActionsMenu, setAnchorElActionsMenu] = useState<null | HTMLElement>(null);
    const [anchorElFilterMenu, setAnchorElFilterMenu] = useState<null | HTMLElement>(null);
    const [showSelectAllIcon, setShowSelectAllIcon] = useState(false);
    const { user, logOut } = React.useContext(AuthContext);
    const { updateNotifications, isNotificationsLoading } = useFetchNotifications();

    const handleMarkAsRead = (notification: Notification) => {
        setLoadingPost(true);
        if (!readStatus || !unreadStatus) {
            return;
        }
        const newStatus = notification.notification_status_id === readStatus.notification_status_id
            ? unreadStatus.notification_status_id
            : readStatus.notification_status_id;
        const readAt = newStatus === readStatus.notification_status_id ? new Date() : null;

        GenericPutResource(`/notifications/${notification.notification_id}`, {
            notification_status_id: newStatus,
            read_at: readAt,
        })
            .then((response) => {
                if (newStatus === readStatus.notification_status_id) {
                    markAsRead(notification);
                } else {
                    markAsUnRead(notification);
                }
                setLoadingPost(false);
            })
            .catch((error) => {
                showSnackBar(error.message, 'error');
                setLoadingPost(false);
            });
    };

    const handleDeleteNotification = (notification: Notification) => {

        if (!deletedStatus) {
            return;
        }
        setLoadingPost(true);
        GenericPutResource(`/notifications/${notification.notification_id}`, {
            notification_status_id: deletedStatus.notification_status_id,
            delete_at: new Date(),
        })
            .then((response) => {
                deleteNotification(notification);
                setLoadingPost(false);
            })
            .catch((error) => {
                showSnackBar(error.message, 'error');
                setLoadingPost(false);
            });
    };

    const handleSelectNotification = (notification: Notification) => {
        const isSelected = selectedNotifications.some(n => n.notification_id === notification.notification_id);
        if (isSelected) {
            setSelectedNotifications(selectedNotifications.filter(n => n.notification_id !== notification.notification_id));
            if (selectedNotifications.length == 0) {
                setShowSelectAllIcon(false);
            }
        } else {
            setSelectedNotifications([...selectedNotifications, notification]);
            setShowSelectAllIcon(true);
        }
    };

    const handleOpenActionsMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElActionsMenu(event.currentTarget);
    };

    const handleCloseActionsMenu = () => {
        setAnchorElActionsMenu(null);
    };

    const handleOpenFilterMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElFilterMenu(event.currentTarget);
    };

    const handleCloseFilterMenu = () => {
        setAnchorElFilterMenu(null);
    };

    const handleMarkSelectedAsRead = () => {
        let notificationsData = {
            notificationsIds: selectedNotifications.map(notification => notification.notification_id).join(','),
            notificationStatus: readStatus?.notification_status_id
        }
        GenericPutResource('/notifications/changenotificationsstatus', notificationsData)
            .then((response) => {
                const notifications: Notification[] = response.data
                notifications.forEach(notification => markAsRead(notification));
                setSelectedNotifications([]);
                handleCloseActionsMenu();
            })
            .catch((error) => {
                showSnackBar(error.message, 'error');
                setLoadingPost(false);
            })
    };

    const handleMarkSelectedAsUnread = () => {
        let notificationsData = {
            notificationsIds: selectedNotifications.map(notification => notification.notification_id).join(','),
            notificationStatus: unreadStatus?.notification_status_id
        }
        GenericPutResource('/notifications/changenotificationsstatus', notificationsData)
            .then((response) => {
                const notifications: Notification[] = response.data
                notifications.forEach(notification => markAsUnRead(notification));
                setSelectedNotifications([]);
                handleCloseActionsMenu();
            })
            .catch((error) => {
                showSnackBar(error.message, 'error');
                setLoadingPost(false);
            })
    };

    const handleDeleteSelected = () => {
        let notificationsData = {
            notificationsIds: selectedNotifications.map(notification => notification.notification_id).join(','),
            notificationStatus: deletedStatus?.notification_status_id
        }
        GenericPutResource('/notifications/changenotificationsstatus', notificationsData)
            .then((response) => {
                const notifications: Notification[] = response.data
                notifications.forEach(notification => deleteNotification(notification));
                setSelectedNotifications([]);
                handleCloseActionsMenu();
            })
            .catch((error) => {
                showSnackBar(error.message, 'error');
                setLoadingPost(false);
            })
    };

    const handleSelectAllIcon = () => {
        if (showSelectAllIcon) {
            setShowSelectAllIcon(false);
            setSelectedNotifications([]);
        } else {
            setShowSelectAllIcon(true);
        }
    }

    useEffect(() => {
        if (!notificationStatuses.some(status => status.notification_status_name === "Todos")) {
            addNotificationStatus(allStatus);
            filterNotificationsByStatus(allStatus);
        }
    }, [])

    useEffect(() => {
        if (selectedStatus) {
            filterNotificationsByStatus(selectedStatus);
        } else {
            filterNotificationsByStatus(allStatus);
        }
    }, [notifications]);

    return (
        <>
            <Grid container xs={12} flexDirection={'column'}>
                <Grid item sx={{ display: 'flex', justifyContent: showSelectAllIcon ? 'space-between' : 'flex-end', paddingX: 2, gap: 2 }}>
                    {showSelectAllIcon && (
                        <Box>
                            <Checkbox
                                checked={selectedNotifications.length === filteredNotifications.length}
                                onChange={() => {
                                    if (selectedNotifications.length === filteredNotifications.length) {
                                        setSelectedNotifications([]);
                                    } else {
                                        setSelectedNotifications(filteredNotifications);
                                    }
                                }}
                            />
                        </Box>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        {selectedNotifications.length > 0 && filteredNotifications.length > 0 && (
                            <Box sx={{ display: 'flex' }}>
                                <Button onClick={handleOpenActionsMenu} aria-haspopup="true" variant='outlined' color='primary'>
                                    {t("generic.notifications.actions.actions")}
                                </Button>
                                <ActionsMenu
                                    anchorElActionsMenu={anchorElActionsMenu}
                                    handleCloseActionsMenu={handleCloseActionsMenu}
                                    handleMarkSelectedAsRead={handleMarkSelectedAsRead}
                                    handleMarkSelectedAsUnread={handleMarkSelectedAsUnread}
                                    handleDeleteSelected={handleDeleteSelected}
                                />
                            </Box>
                        )}
                        {filteredNotifications.length > 0 && (
                            <Box>
                                <Tooltip title={t("generic.notifications.actions.select")} placement='top'>
                                    <IconButton onClick={() => handleSelectAllIcon()}>
                                        <ContentCopyIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )
                        }
                        {notificationStatuses.length > 0 && (
                            <Box>
                                <Tooltip title={t("generic.notifications.actions.filter-notifications")} placement='top'>
                                    <IconButton onClick={handleOpenFilterMenu}>
                                        <FilterListIcon />
                                    </IconButton>
                                </Tooltip>
                                <FilterMenu
                                    anchorElFilterMenu={anchorElFilterMenu}
                                    handleCloseFilterMenu={handleCloseFilterMenu}
                                    notificationStatuses={notificationStatuses}
                                    filterNotificationsByStatus={filterNotificationsByStatus}
                                    setSelectedStatus={setSelectedStatus}
                                />
                            </Box>
                        )}
                        <Box>
                            <Tooltip title={t("generic.notifications.actions.update-notifications")} placement='top'>
                                <IconButton onClick={() => {
                                    updateNotifications();
                                    if (selectedStatus) {
                                        filterNotificationsByStatus(selectedStatus);
                                    } else {
                                        filterNotificationsByStatus(allStatus);
                                    }
                                }} disabled={isNotificationsLoading}>
                                    < ReplayIcon />
                                </IconButton >
                            </Tooltip>
                        </Box>
                    </Box>
                </Grid>
                <Grid item>
                    <NotificationList
                        filteredNotifications={filteredNotifications}
                        selectedNotifications={selectedNotifications}
                        handleSelectNotification={handleSelectNotification}
                        handleMarkAsRead={handleMarkAsRead}
                        handleDeleteNotification={handleDeleteNotification}
                        loadingPost={loadingPost}
                        showSelectAllIcon={showSelectAllIcon}
                    />
                </Grid>
            </Grid>
            <SnackbarComponent />
        </>
    );
}
